import React from 'react';
import { Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { colors, spacing, createFlexCenter } from '../../styles/theme';

const { Text } = Typography;

interface LoadingSpinnerProps {
  /** 로딩 메시지 */
  message?: string;
  /** 스피너 크기 */
  size?: 'small' | 'default' | 'large';
  /** 전체 화면 중앙 배치 여부 */
  fullScreen?: boolean;
  /** 최소 높이 */
  minHeight?: number;
  /** 커스텀 스타일 */
  style?: React.CSSProperties;
  /** 커스텀 아이콘 */
  indicator?: React.ReactElement;
}

/**
 * 통합 로딩 스피너 컴포넌트
 * 
 * 일관된 로딩 상태를 표시하기 위한 공통 컴포넌트입니다.
 * 다양한 크기와 스타일을 지원하며, 접근성을 고려한 설계입니다.
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = React.memo(({
  message = '로딩 중...',
  size = 'default',
  fullScreen = false,
  minHeight = 200,
  style,
  indicator,
}) => {
  const getSizeConfig = () => {
    switch (size) {
      case 'small':
        return { fontSize: 16, textSize: 12 };
      case 'large':
        return { fontSize: 32, textSize: 16 };
      default:
        return { fontSize: 24, textSize: 14 };
    }
  };

  const sizeConfig = getSizeConfig();
  
  const defaultIndicator = (
    <LoadingOutlined 
      style={{ 
        fontSize: sizeConfig.fontSize, 
        color: colors.primary[500] 
      }} 
      spin 
    />
  );

  const containerStyle: React.CSSProperties = {
    ...createFlexCenter(),
    flexDirection: 'column',
    gap: spacing[3],
    minHeight: fullScreen ? '100vh' : minHeight,
    width: '100%',
    padding: spacing[4],
    ...style,
  };

  return (
    <div 
      style={containerStyle}
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      <Spin 
        indicator={indicator || defaultIndicator}
        size={size}
      />
      {message && (
        <Text 
          type="secondary" 
          style={{ 
            fontSize: sizeConfig.textSize,
            textAlign: 'center',
            marginTop: spacing[2],
          }}
        >
          {message}
        </Text>
      )}
    </div>
  );
});

LoadingSpinner.displayName = 'LoadingSpinner';

export default LoadingSpinner;